import { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Business, EmojiEvents } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataTable } from './data-table';
import type { OrganizationStatsResponse } from '@store/generated/jotcoach';

interface JotCoachOrgLeaderboardProps {
  organizations: OrganizationStatsResponse[];
  loading?: boolean;
}

export function JotCoachOrgLeaderboard({
  organizations,
  loading = false,
}: JotCoachOrgLeaderboardProps) {
  const navigate = useNavigate();

  const getMedalIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const columns = useMemo(
    () => [
      {
        key: 'rank' as keyof OrganizationStatsResponse,
        label: 'Rank',
        render: (_: unknown, row: OrganizationStatsResponse, index?: number) => {
          const rank = (index || 0) + 1;
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" fontWeight="bold" fontSize="1.2rem">
                {getMedalIcon(rank)}
              </Typography>
            </Box>
          );
        },
      },
      {
        key: 'organization_name' as keyof OrganizationStatsResponse,
        label: 'Organization',
        render: (_: unknown, row: OrganizationStatsResponse) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Business fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {row.organization_name || 'Unknown Organization'}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {row.organization_id.substring(0, 12)}...
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        key: 'session_count' as keyof OrganizationStatsResponse,
        label: 'Sessions',
        render: (value: unknown) => (
          <Chip
            label={typeof value === 'number' ? value.toLocaleString() : String(value)}
            color="primary"
            size="small"
          />
        ),
      },
      {
        key: 'unique_users' as keyof OrganizationStatsResponse,
        label: 'Providers',
        render: (value: unknown) => (
          <Typography variant="body2" color="text.secondary">
            {typeof value === 'number' ? value.toLocaleString() : String(value)}
          </Typography>
        ),
      },
      {
        key: 'feedback_count' as keyof OrganizationStatsResponse,
        label: 'Total Feedback',
        render: (value: unknown) => (
          <Typography variant="body2">
            {typeof value === 'number' ? value.toLocaleString() : String(value)}
          </Typography>
        ),
      },
      {
        key: 'feedback_rate' as keyof OrganizationStatsResponse,
        label: 'Feedback Rate',
        render: (value: unknown) => {
          const rate = typeof value === 'number' ? value : 0;
          return (
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {rate.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                per session
              </Typography>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const handleRowClick = (org: OrganizationStatsResponse) => {
    navigate(`/admin-v2/jotcoach/sessions?organization_id=${org.organization_id}`);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <EmojiEvents fontSize="large" color="primary" />
        <Box>
          <Typography variant="h5" fontWeight="medium">
            Organization Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Top 10 organizations by session count
          </Typography>
        </Box>
      </Box>

      <DataTable
        title=""
        columns={columns}
        data={organizations}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage="No organization data available for the selected period"
      />
    </Box>
  );
}
